import type { ActivityFilter, DealSearch } from './endpoints';
import type { Activity, AppNotification, Deal } from './types';

// My day, the same on the web app's start page (TF-61) and on the phone's Home (TF-273): which calls
// it makes, and how it reads what they answer. Today is the viewer's: the web app's zone setting
// (TF-220), the phone's own zone. The caller gives the day's bounds in that zone and a way to read the
// day an instant falls on there; nothing here knows a zone, so both apps get the same answer for the
// same person, company and day. Pure, no imports from either app.

/** Today (or a few days from today) in the viewer's zone: [from, to) as instants and as dates. */
export interface DayBounds {
  /** The viewer's midnight that starts the first day, and the one after the last. */
  from: Date;
  to: Date;
  /** The same as YYYY-MM-DD: deals close on a date, not at a moment. */
  fromDay: string;
  toDay: string;
}

/** The three calls of My day: my open tasks due in the days, my overdue ones, my deals closing then. */
export function myDayFilters(bounds: DayBounds): { tasks: ActivityFilter; overdue: ActivityFilter; deals: DealSearch } {
  const mine = { type: 'task', open: true, assignedTo: 'me', sort: 'due' } as const;
  return {
    tasks: { ...mine, dueFrom: bounds.from.toISOString(), dueTo: bounds.to.toISOString() },
    // Everything due before today's midnight and still open, oldest first.
    overdue: { ...mine, dueTo: bounds.from.toISOString() },
    deals: { status: 'open', ownerId: 'me', closeFrom: bounds.fromDay, closeTo: bounds.toDay },
  };
}

/** How many of each the calls ask for: what one screen of My day can show and count. */
export const MY_DAY_LIMITS = { tasks: 100, overdue: 100, deals: 50 } as const;

/** Reads the day an instant falls on, YYYY-MM-DD, in the viewer's zone. */
export type DayOf = (instant: string) => string;

/** The day a task is on for the viewer: its day when due "at any time", else the day its time falls on; null undated. */
export function taskDay(task: Pick<Activity, 'dueAt' | 'dueDay'>, dayOf: DayOf): string | null {
  if (task.dueDay) return task.dueDay;
  return task.dueAt ? dayOf(task.dueAt) : null;
}

export interface DayPlan {
  /** Tasks with a time, in time order. */
  timed: Activity[];
  /** Tasks due that day "at any time". */
  anyTime: Activity[];
  /** My open deals expected to close that day. */
  deals: Deal[];
}

/** One day of My day: its timed tasks, then those due that day, then the deals that close on it. */
export function planOfDay(tasks: readonly Activity[], deals: readonly Deal[], day: string, dayOf: DayOf): DayPlan {
  const ofDay = tasks.filter((task) => task.dueAt && taskDay(task, dayOf) === day);
  return {
    timed: ofDay.filter((task) => !task.dueDay).sort((a, b) => Date.parse(a.dueAt!) - Date.parse(b.dueAt!)),
    anyTime: ofDay.filter((task) => task.dueDay),
    deals: deals.filter((deal) => deal.expectedCloseDate === day),
  };
}

/** Nothing at all planned that day. */
export const isEmptyPlan = (plan: DayPlan) => plan.timed.length + plan.anyTime.length + plan.deals.length === 0;

/** A meeting is a task of the kind "meeting" (TF-10). */
export const isMeeting = (task: Pick<Activity, 'category'>) => task.category === 'meeting';

/** Open tasks due on a day before today: overdue, whatever their time was. */
export function overdueOf(tasks: readonly Activity[], today: string, dayOf: DayOf): Activity[] {
  return tasks.filter((task) => {
    const day = taskDay(task, dayOf);
    return task.doneAt === null && day !== null && day < today;
  });
}

/** Where a task belongs, most specific first: the name the web app links it by. */
export const recordNameOf = (task: Pick<Activity, 'dealTitle' | 'contactName' | 'accountName'>): string | null => task.dealTitle ?? task.contactName ?? task.accountName ?? null;

/** The number on the bell and on the phone's Home tab: nothing at none, "999+" past 999. */
export function unreadBadge(count: number, max = 999): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  return count > max ? `${max}+` : String(Math.floor(count));
}

/** The unread ones, newest first: what Home shows of the bell. */
export const unreadNotifications = (items: readonly AppNotification[]) => items.filter((item) => !item.readAt).sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));

/** One notification read now, the rest as they were. */
export const markedRead = (items: readonly AppNotification[], id: string, at: string): AppNotification[] => items.map((item) => (item.id === id && !item.readAt ? { ...item, readAt: at } : item));

/** Every notification read now; and the ids that were unread, to undo it. */
export function allMarkedRead(items: readonly AppNotification[], at: string): { items: AppNotification[]; ids: string[] } {
  const ids = items.filter((item) => !item.readAt).map((item) => item.id);
  return { items: items.map((item) => (item.readAt ? item : { ...item, readAt: at })), ids };
}

/** Pages of a list, joined: a later page never repeats what an earlier one had. */
export function joinPages<T extends { id: string }>(earlier: readonly T[], later: readonly T[]): T[] {
  const seen = new Set(earlier.map((item) => item.id));
  return [...earlier, ...later.filter((item) => !seen.has(item.id))];
}
